import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAutoplayVideo } from "@/core/utils/autoplay-videos";
import {
  animateHeroHeading,
  animateHeroSubheading,
  animateCtaButtons,
  animateOverlay,
  cleanupAnimations,
} from "./gsap";

const WelcomeBanner = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const subheadingRef = useRef<HTMLParagraphElement>(null);
  const ctaContainerRef = useRef<HTMLDivElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [posterLoaded, setPosterLoaded] = useState(false);

  // Use the autoplay video hook to ensure the video plays
  useAutoplayVideo(videoRef);

  // Use effect to properly position the hero section on mount and resize
  useEffect(() => {
    const fixHeroPosition = () => {
      if (!sectionRef.current) return;

      // Get actual header height
      const headerHeight = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--header-total-height"
        ) || "84"
      );

      // Get all header elements to ensure proper z-index
      const headerElements = document.querySelectorAll("header");
      headerElements.forEach((header) => {
        if (header) header.style.zIndex = "100";
      });

      // Set precise positioning for the hero section
      sectionRef.current.style.marginTop = "0";
      sectionRef.current.style.paddingTop = `${headerHeight}px`;
    };

    // Run once on mount
    fixHeroPosition();

    // Add resize listener
    window.addEventListener("resize", fixHeroPosition);

    // Cleanup
    return () => {
      window.removeEventListener("resize", fixHeroPosition);
    };
  }, []);

  // Animation effect
  useEffect(() => {
    // Apply animations with a small timeout to ensure DOM is ready
    const animationTimeout = setTimeout(() => {
      if (
        headingRef.current &&
        subheadingRef.current &&
        ctaContainerRef.current &&
        overlayRef.current
      ) {
        animateHeroHeading({ current: headingRef.current });
        animateHeroSubheading({ current: subheadingRef.current });
        animateCtaButtons({ current: ctaContainerRef.current });
        animateOverlay({ current: overlayRef.current });
      }
    }, 100);

    return () => {
      clearTimeout(animationTimeout);
      cleanupAnimations();
    };
  }, []);

  // Handler for poster image load
  const handlePosterLoad = () => {
    setPosterLoaded(true);
    console.log("✅ Hero poster image loaded successfully");
  };

  return (
    <section
      ref={sectionRef}
      aria-labelledby="welcome-hero-title"
      className="relative bg-law-black text-white overflow-hidden h-screen max-h-[800px] min-h-[600px]"
    >
      {/* Background wrapper that will extend under the header */}
      <div
        className="absolute inset-0 top-[-var(--header-total-height)] bottom-0 left-0 right-0 w-full"
        style={{ height: "calc(100% + var(--header-total-height))" }}
        aria-hidden="true"
      >
        {/* Poster image with immediate load - CONVERTED TO IMG ELEMENT FOR LCP OPTIMIZATION */}
        <div
          className={`absolute inset-0 w-full h-full overflow-hidden ${
            videoLoaded ? "opacity-0" : "opacity-100"
          } transition-opacity duration-500`}
        >
          <img
            src="/videos/street-lawyer-services-cannabis-festival-washington-dc-poster.webp"
            alt=""
            className="w-full h-full object-cover"
            fetchPriority="high"
            loading="eager"
            onLoad={handlePosterLoad}
            aria-hidden="true"
          />
        </div>

        {/* Video */}
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover ${
            videoLoaded ? "opacity-100" : "opacity-0"
          } transition-opacity duration-500`}
          autoPlay
          muted
          loop
          playsInline
          poster="/videos/street-lawyer-services-cannabis-festival-washington-dc-poster.jpg"
          onLoadedData={() => setVideoLoaded(true)}
          onCanPlayThrough={() => setVideoLoaded(true)}
          aria-hidden="true"
        >
          <source
            src="/videos/street-lawyer-services-cannabis-festival-washington-dc.mp4"
            type="video/mp4"
          />
          Your browser does not support the video tag.
        </video>

        {/* Overlay */}
        <div
          ref={overlayRef}
          className="absolute inset-0 bg-gradient-to-b from-law-black/80 to-law-black/60"
          aria-hidden="true"
        ></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center justify-center">
        <div className="container mx-auto px-4 text-center">
          <h1
            ref={headingRef}
            id="welcome-hero-title"
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 min-h-[3.5rem] md:min-h-[4rem] lg:min-h-[4.5rem]"
          >
            Street Lawyer Services
          </h1>
          <p
            ref={subheadingRef}
            className="text-lg md:text-xl lg:text-2xl mb-8 max-w-3xl mx-auto min-h-[3rem] md:min-h-[3.5rem]"
          >
            DC's premier cannabis dispensary offering high-quality products for
            adult consumers
          </p>
          <div
            ref={ctaContainerRef}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/products"
              className="btn-primary"
              aria-label="Browse our cannabis products including flower, edibles, and concentrates"
            >
              View Products
            </Link>
            <Link
              to="/about"
              className="border-2 border-white text-white hover:bg-white hover:text-law-black font-semibold py-3 px-8 rounded-lg transition-colors duration-300"
              aria-label="Learn more about Street Lawyer Services cannabis dispensary"
            >
              Learn More
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WelcomeBanner;
